/**
 * Backend documents API — list + detail + upload hooks.
 *
 * Wraps `GET /v1/documents` and `GET /v1/documents/{id}`. blobPath is
 * admin-only — non-admin viewers see the field omitted.
 *
 * Upload (2026-05-21, FE-2 / Henry R2-002): wires the WEB uploader to the
 * same SAS flow the mobile app already uses — `POST /v1/documents/upload-init`
 * mints a 15-minute Azure Storage SAS URL, the client PUTs the bytes straight
 * to that URL (the SAS token is the credential — no bearer header), then
 * `POST /v1/documents/{id}/upload-finalize` flips status to `verified`. The
 * backend writes the audit entries (`document.uploaded` + `document.finalized`)
 * automatically. Mirrors `mobile/src/lib/api-data.ts` (`apiUploadInit` /
 * `putToSasUrl` / `apiUploadFinalize`) + `mobile/src/lib/queries`
 * (`useUploadInit` / `useUploadFinalize`).
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './client';

export type DocumentStatus = 'pending-scan' | 'verified' | 'rejected-malware' | 'rejected-other';

export interface DocumentView {
  id: string;
  tenantId: string;
  trustId?: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  status: DocumentStatus;
  eSignAllowed: boolean;
  documentType: string;
  /** Admin-only. */
  blobPath?: string;
  /** Admin firm-wide scope only — the doc's trust display name (absent when
   *  unfiled; still present + `trustArchived` when the trust is soft-deleted). */
  trustName?: string;
  /** Admin firm-wide scope only — true when the doc has no `trustId` (an orphan
   *  / tenant-wide upload). The "Assign to trust" action targets these. */
  isUnfiled?: boolean;
  /** Admin firm-wide scope only — true when the doc's trust was soft-deleted.
   *  Reachable here + re-homeable via the assign action. */
  trustArchived?: boolean;
  /** Admin / firm-wide scope only — true when the DOCUMENT itself is
   *  soft-archived (superseded or redundant, retained for compliance).
   *  Until 2026-08-22 archived docs sat in the firm-wide lists with NO flag
   *  while the per-trust vault hid them: the same document, two stories.
   *  Only ever returned when the caller opted in with `includeArchived`. */
  archived?: boolean;
  /** Firm-wide aggregate (GET /v1/firm/documents) only — the owning CLIENT tenant
   *  under first-class tenancy. The firm-wide board groups + tags docs by client. */
  clientTenantId?: string;
  /** Firm-wide aggregate only — the owning client's display name. */
  clientDisplayName?: string;
  uploadedByUserId: string;
  uploadedAt: string;
  scannedAt?: string;
  scanResult?: string;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentsListResponse {
  documents: DocumentView[];
  /** Admin firm-wide only — total matching the filters BEFORE pagination. */
  total?: number;
  correlationId: string;
}

export interface DocumentGetResponse {
  document: DocumentView;
  correlationId: string;
}

export function useDocuments(opts: { trustId?: string; enabled?: boolean } = {}) {
  const qs = opts.trustId ? `?trustId=${encodeURIComponent(opts.trustId)}` : '';
  return useQuery<DocumentsListResponse>({
    queryKey: ['documents', 'list', opts.trustId ?? null],
    queryFn: ({ signal }) =>
      api<DocumentsListResponse>(`/api/v1/documents${qs}`, { signal }),
    enabled: opts.enabled ?? true,
  });
}

export function useDocument(id: string | undefined) {
  return useQuery<DocumentGetResponse>({
    queryKey: ['documents', 'detail', id ?? null],
    queryFn: ({ signal }) =>
      api<DocumentGetResponse>(`/api/v1/documents/${encodeURIComponent(id!)}`, {
        signal,
      }),
    enabled: !!id,
  });
}

// ── Download: mint a read SAS + open (DOC-READBACK) ─────────────────────────
//
// `GET /v1/documents/{id}/download` (handlers/documents-download.ts) mints a
// 15-minute read-only Azure Storage SAS URL. `downloadUrl` is `null` (with a
// `notice`) when DOCUMENTS_STORAGE_ACCOUNT is unset (pre-infra dev) — the caller
// shows the notice rather than a fake success. Modelled as a mutation because it
// is an imperative user action (a button click), not page data.

export interface DocumentDownloadResponse {
  documentId: string;
  fileName: string;
  contentType: string;
  /** 15-min read SAS URL; null (+ notice) when storage isn't configured yet. */
  downloadUrl: string | null;
  expiresAt?: string;
  notice?: string;
  correlationId: string;
}

export function useDocumentDownload() {
  return useMutation<DocumentDownloadResponse, ApiError, { documentId: string }>({
    mutationFn: ({ documentId }) =>
      api<DocumentDownloadResponse>(
        `/api/v1/documents/${encodeURIComponent(documentId)}/download`,
      ),
  });
}

/** Open a downloaded document: navigate to the SAS URL in a new tab when
 *  present, else surface the backend notice via the supplied toast sink.
 *  Centralised so the vault, LoA/LoW and any future surface behave identically. */
export function openDownloadedDocument(
  res: DocumentDownloadResponse,
  onNotice: (message: string) => void,
): void {
  if (res.downloadUrl) {
    window.open(res.downloadUrl, '_blank', 'noopener,noreferrer');
  } else {
    onNotice(res.notice ?? 'This document is not available to download yet.');
  }
}

// ── In-app source bytes (P2-5 "see it on the document") ──────────────────────
//
// The "see it on the document" pane renders the source bytes INLINE (PDF.js /
// <img>) with the read region highlighted — so it can't just `window.open` the
// SAS like the vault download. It needs the actual bytes as a blob. This hook
// reuses the SAME read-SAS endpoint (`GET /v1/documents/{id}/download`) the vault
// uses, then fetches the bytes straight off the SAS URL into an object URL the
// viewer can hand to PDF.js or an <img>.
//
// State machine the pane renders:
//   - 'loading'      — minting the SAS + fetching the bytes;
//   - 'ready'        — `objectUrl` + `contentType` available;
//   - 'unavailable'  — backend returned downloadUrl:null (storage not wired in
//                      this env) → the pane shows the honest notice, not a fake;
//   - 'error'        — the SAS expired / 404 / a network failure → graceful error.
//
// The object URL is revoked on unmount / when the document changes so we don't
// leak blob handles. Cross-origin: the SAS URL is an Azure Storage URL with the
// token as the credential — fetched WITHOUT the Supabase bearer (exactly like
// `putBytesToSas`), so no auth header is attached.

export type DocumentSourceState =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'ready'; objectUrl: string; contentType: string; fileName: string }
  | { phase: 'unavailable'; notice: string }
  | { phase: 'error'; message: string };

/** True when the content-type is a PDF (drives PDF.js vs <img> in the viewer). */
export function isPdfContentType(contentType: string | undefined): boolean {
  return !!contentType && contentType.toLowerCase().includes('pdf');
}

/** True when the content-type is a renderable raster image. */
export function isImageContentType(contentType: string | undefined): boolean {
  return !!contentType && contentType.toLowerCase().startsWith('image/');
}

/**
 * Fetch a document's bytes for inline viewing (read-SAS → blob → object URL).
 *
 * `enabled:false` (e.g. the pane isn't open) defers all work. The fetch is the
 * platform `fetch` (NOT `api()`) because the SAS token is the credential. Any
 * failure resolves to a graceful `error`/`unavailable` phase — the pane never
 * throws. The returned object URL is owned by this hook and revoked on
 * unmount/change.
 */
export function useDocumentSource(
  documentId: string | undefined,
  opts: { enabled?: boolean } = {},
): DocumentSourceState {
  const enabled = (opts.enabled ?? true) && !!documentId;
  const [state, setState] = useState<DocumentSourceState>({ phase: 'idle' });
  // Track the live object URL so we can revoke it on change/unmount.
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setState({ phase: 'idle' });
      return;
    }
    let cancelled = false;
    setState({ phase: 'loading' });

    (async () => {
      try {
        const res = await api<DocumentDownloadResponse>(
          `/api/v1/documents/${encodeURIComponent(documentId!)}/download`,
        );
        if (cancelled) return;
        if (!res.downloadUrl) {
          setState({
            phase: 'unavailable',
            notice:
              res.notice ??
              'This document is not available to view yet. Please try again later.',
          });
          return;
        }
        // The SAS URL is the credential — fetch WITHOUT the Supabase bearer.
        const blobRes = await fetch(res.downloadUrl);
        if (cancelled) return;
        if (!blobRes.ok) {
          setState({
            phase: 'error',
            message:
              blobRes.status === 403 || blobRes.status === 404
                ? 'This document link has expired. Close and reopen to try again.'
                : `Could not load the document (${blobRes.status}).`,
          });
          return;
        }
        const blob = await blobRes.blob();
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(blob);
        // Revoke any previous URL before swapping in the new one.
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = objectUrl;
        setState({
          phase: 'ready',
          objectUrl,
          // Prefer the blob's own type; fall back to the download metadata.
          contentType: blob.type || res.contentType,
          fileName: res.fileName,
        });
      } catch (err) {
        if (cancelled) return;
        setState({
          phase: 'error',
          message:
            err instanceof ApiError
              ? 'We could not open this document. You may not have access, or it has been removed.'
              : 'Could not load the document. Check your connection and try again.',
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [documentId, enabled]);

  // Revoke the object URL on unmount (separate effect so it isn't torn down on
  // every state change — only when the component leaves).
  useEffect(() => {
    return () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, []);

  return state;
}

// ── Upload: init → SAS PUT → finalize ───────────────────────────────────────
//
// Mirrors the mobile flow (mobile/src/lib/api-data.ts). The backend handlers
// are `documents-upload-init.ts` (POST /v1/documents/upload-init) and
// `documents-upload-finalize.ts` (POST /v1/documents/{id}/upload-finalize).

export interface UploadInitRequest {
  filename: string;
  contentType: string;
  sizeBytes: number;
  documentType: string;
  /** Omit for a tenant-wide upload (admin-only); supply to scope to a trust. */
  trustId?: string;
  /** REV-19: SHA-256 (hex) of the file bytes, so the same file dropped twice is
   *  answered as a duplicate instead of ingested again. Omitted when the browser
   *  cannot hash (no Web Crypto); the upload then proceeds unchecked. */
  contentSha256?: string;
}

export interface UploadInitResponse {
  document: DocumentView;
  /**
   * 15-minute Azure Storage SAS URL the bytes are PUT to. `null` when
   * DOCUMENTS_STORAGE_ACCOUNT is unset (pre-infra dev) — the DocumentDoc is
   * still created, the bytes simply aren't transmitted until storage is wired.
   */
  uploadUrl: string | null;
  expiresAt?: string;
  /** Present iff `uploadUrl === null` — explains why SAS minting was skipped. */
  notice?: string;
  correlationId: string;
  /** REV-19: present (with `uploadUrl: null`) when a verified document with the
   *  same bytes is already on record in this tenant. Nothing new was minted. */
  duplicateOf?: { documentId: string; filename: string; uploadedAt: string };
}

export interface UploadFinalizeResponse {
  document: DocumentView;
  correlationId: string;
}

/**
 * PUT the file bytes to the Azure Storage SAS URL minted by upload-init.
 *
 * Uses the platform `fetch` directly — NOT the shared `api()` client — because
 * the SAS token in the URL is the credential: attaching the Supabase bearer
 * header would be rejected by Azure Storage. Azure block-blob uploads require
 * the `x-ms-blob-type: BlockBlob` header (REST contract). Throws an `ApiError`
 * on a non-2xx so call sites can route it through `mapError` like every other
 * failure.
 */
export async function putBytesToSas(args: {
  sasUrl: string;
  file: Blob;
  contentType: string;
}): Promise<void> {
  const res = await fetch(args.sasUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'content-type': args.contentType,
    },
    body: args.file,
  });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.text();
    } catch {
      body = null;
    }
    throw new ApiError(
      res.status,
      body,
      'SAS_UPLOAD_FAILED',
      undefined,
      `SAS upload failed: ${res.status}`,
    );
  }
}

/**
 * upload-init mutation. Registers the pending DocumentDoc + mints the SAS URL.
 * Pairs with `putBytesToSas` (the PUT) + `useDocumentUploadFinalize`.
 */
export function useDocumentUploadInit() {
  return useMutation<UploadInitResponse, ApiError, UploadInitRequest>({
    mutationFn: (body) =>
      api<UploadInitResponse>('/api/v1/documents/upload-init', {
        method: 'POST',
        body,
      }),
  });
}

/**
 * upload-finalize mutation. Flips the document status to `verified` server-side
 * and invalidates the documents queries so the new row appears in the list. The
 * trust-scoped list (`useTrustDocuments`, key `['trusts','documents',trustId]`)
 * is what the documents screen renders; the flat `['documents']` list is
 * invalidated too for any admin surface that reads it.
 */
export function useDocumentUploadFinalize() {
  const qc = useQueryClient();
  return useMutation<
    UploadFinalizeResponse,
    ApiError,
    { documentId: string; trustId?: string }
  >({
    mutationFn: ({ documentId }) =>
      api<UploadFinalizeResponse>(
        `/api/v1/documents/${encodeURIComponent(documentId)}/upload-finalize`,
        { method: 'POST', body: {} },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
      if (vars.trustId) {
        void qc.invalidateQueries({
          queryKey: ['trusts', 'documents', vars.trustId],
        });
      }
    },
  });
}

// ── Firm-wide documents aggregate (first-class tenancy, 2026-07-09) ───────────
//
// `GET /v1/firm/documents` — backs /admin/documents. Under first-class-per-client
// tenancy every client is its OWN tenant, so a firm's documents live across many
// client tenants; the firm-console board must AGGREGATE them. The backend
// enumerates the firm's granted client tenants (grant-verified) and fans out a
// read-only documents read into each, tagging every row with its owning
// `clientTenantId` + `clientDisplayName`. Read-only: the firm cannot file/move
// from here (those are writes into a client tenant, and need act-as). Server-side
// `q` (filename) + `documentType` filters narrow the aggregated book.
// Tenant-wide-reader gated on the backend (admin / compliance / director /
// super_admin).

/** A client the firm-wide board aggregated (grant-verified). */
export interface FirmDocumentsClientRef {
  clientTenantId: string;
  clientDisplayName: string;
}

export interface FirmDocumentsResponse {
  documents: DocumentView[];
  /** The clients actually aggregated this request (grant-verified, capped). */
  clients: FirmDocumentsClientRef[];
  /** Active client links in the firm's book (the "of M" denominator). */
  totalClients: number;
  /** Clients actually read this request (after cap + grant verification). */
  shownClients: number;
  /** True when the book exceeded the fan-out cap and was truncated. */
  truncated: boolean;
  /** Documents after the q/documentType filters. */
  total: number;
  correlationId: string;
}

export interface FirmDocumentsFilters {
  /** Exact-match documentType facet across the aggregated book. */
  documentType?: string;
  /** Case-insensitive filename substring across the aggregated book. */
  q?: string;
  /** Include soft-archived documents (each flagged `archived: true`). Excluded
   *  by default since 2026-08-22 — they used to appear here unflagged. */
  includeArchived?: boolean;
  enabled?: boolean;
}

function buildFirmDocsQuery(f: Omit<FirmDocumentsFilters, 'enabled'>): string {
  const p = new URLSearchParams();
  if (f.documentType) p.set('documentType', f.documentType);
  if (f.q && f.q.trim()) p.set('q', f.q.trim());
  if (f.includeArchived) p.set('includeArchived', 'true');
  const s = p.toString();
  return s ? `?${s}` : '';
}

export function useFirmDocuments(filters: FirmDocumentsFilters = {}) {
  const { enabled, ...f } = filters;
  const qs = buildFirmDocsQuery(f);
  return useQuery<FirmDocumentsResponse>({
    // Keyed on the serialized filters so each filter combination is cached
    // independently (and refetches when a filter changes).
    queryKey: ['documents', 'firm', qs],
    queryFn: ({ signal }) => api<FirmDocumentsResponse>(`/api/v1/firm/documents${qs}`, { signal }),
    enabled: enabled ?? true,
  });
}

// ── Assign / move to a trust (home an unfiled doc OR move a filed one) ────────
//
// `PATCH /v1/documents/{id}/trust` (handlers/documents-assign-trust.ts). Sets the
// doc's `trustId`. Two jobs share this one endpoint:
//   - ASSIGN  — home an UNFILED / archived-trust doc onto a trust.
//   - MOVE    — re-file a doc that already sits under a trust onto a DIFFERENT
//               trust (the P2-6 mis-attribution fix). The backend handles
//               filed→different-trust + records `fromTrustId`→`toTrustId` in the
//               `document.reassigned` audit, so the move trail is complete.
//
// The optional `fromTrustId` is the doc's CURRENT trust on a move. It is NOT sent
// on the wire (the endpoint accepts only `{ trustId }`) — it exists purely so the
// success handler can also invalidate the OLD trust's vault, which the doc has
// just left. Always invalidates the firm-wide list (the row's trust column
// changes / it leaves the Unfiled filter) + the destination trust's vault.

export interface AssignDocumentResponse {
  document: {
    id: string;
    tenantId: string;
    trustId?: string;
    filename: string;
    documentType: string;
    status: DocumentStatus;
  };
  correlationId: string;
}

export interface AssignDocumentToTrustInput {
  documentId: string;
  /** The destination trust. */
  trustId: string;
  /** The doc's CURRENT trust on a MOVE — used only to refresh the source vault
   *  (never sent on the wire). Omit for an unfiled assign. */
  fromTrustId?: string;
}

export function useAssignDocumentToTrust() {
  const qc = useQueryClient();
  return useMutation<AssignDocumentResponse, ApiError, AssignDocumentToTrustInput>({
    // Only `trustId` is sent — `fromTrustId` is destructured away so it can never
    // leak into the request body (the endpoint's schema is `.strict()`).
    mutationFn: ({ documentId, trustId }) =>
      api<AssignDocumentResponse>(
        `/api/v1/documents/${encodeURIComponent(documentId)}/trust`,
        { method: 'PATCH', body: { trustId } },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
      void qc.invalidateQueries({ queryKey: ['trusts', 'documents', vars.trustId] });
      // On a move, the document left its old trust — refresh that vault too (skip
      // a no-op same-trust value, though the UI never offers that).
      if (vars.fromTrustId && vars.fromTrustId !== vars.trustId) {
        void qc.invalidateQueries({ queryKey: ['trusts', 'documents', vars.fromTrustId] });
      }
    },
  });
}

// ── Archive (soft-archive a redundant document) ──────────────────────────────
//
// `PATCH /v1/documents/{id}/archive` (handlers/documents-archive.ts). Soft-archives
// a redundant (superseded / duplicate) document — RETAINED but hidden from the
// vault's default list (the "show archived" toggle brings it back). NOT a delete.
// Invalidates the trust's vault queries so the row leaves the default view.

export interface ArchiveDocumentResponse {
  document: {
    id: string;
    tenantId: string;
    trustId?: string;
    filename: string;
    documentType: string;
    status: DocumentStatus;
    archivedAt?: string;
    archivedReason?: string;
  };
  correlationId: string;
}

export function useArchiveDocument() {
  const qc = useQueryClient();
  return useMutation<
    ArchiveDocumentResponse,
    ApiError,
    { documentId: string; trustId: string; reason?: string }
  >({
    mutationFn: ({ documentId, reason }) =>
      api<ArchiveDocumentResponse>(
        `/api/v1/documents/${encodeURIComponent(documentId)}/archive`,
        { method: 'PATCH', body: reason ? { reason } : {} },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
      void qc.invalidateQueries({ queryKey: ['trusts', 'documents', vars.trustId] });
    },
  });
}

// ── Unarchive (restore an archived document) ─────────────────────────────────
//
// `PATCH /v1/documents/{id}/unarchive` (handlers/documents-unarchive.ts). The
// reversal of archive (SUPERSEDE-SPEC §4): clears the archive triplet so the doc
// returns to the vault's default list. Idempotent; refuses a tombstoned doc.

export interface UnarchiveDocumentResponse {
  document: {
    id: string;
    tenantId: string;
    trustId?: string;
    filename: string;
    documentType: string;
    status: DocumentStatus;
  };
  correlationId: string;
}

export function useUnarchiveDocument() {
  const qc = useQueryClient();
  return useMutation<
    UnarchiveDocumentResponse,
    ApiError,
    { documentId: string; trustId: string }
  >({
    mutationFn: ({ documentId }) =>
      api<UnarchiveDocumentResponse>(
        `/api/v1/documents/${encodeURIComponent(documentId)}/unarchive`,
        { method: 'PATCH', body: {} },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
      void qc.invalidateQueries({ queryKey: ['trusts', 'documents', vars.trustId] });
    },
  });
}

// ── Retype (correct a misclassified document) ────────────────────────────────
//
// `PATCH /v1/documents/{id}/retype` (handlers/documents-retype.ts). Corrects the
// `documentType` that drove a wrong supersede clustering (SUPERSEDE-SPEC §4). The
// vault badge recomputes on the next read. Registry-validated; refuses a
// tombstoned doc.

export interface RetypeDocumentResponse {
  document: {
    id: string;
    tenantId: string;
    trustId?: string;
    filename: string;
    documentType: string;
    status: DocumentStatus;
  };
  correlationId: string;
}

export function useRetypeDocument() {
  const qc = useQueryClient();
  return useMutation<
    RetypeDocumentResponse,
    ApiError,
    { documentId: string; trustId: string; documentType: string }
  >({
    mutationFn: ({ documentId, documentType }) =>
      api<RetypeDocumentResponse>(
        `/api/v1/documents/${encodeURIComponent(documentId)}/retype`,
        { method: 'PATCH', body: { documentType } },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
      void qc.invalidateQueries({ queryKey: ['trusts', 'documents', vars.trustId] });
    },
  });
}

// ── Rescan (re-drive a stuck or rejected scan) — lane C · C3, 2026-07-09 ──────
//
// `POST /v1/admin/documents/{id}/rescan` (handlers/admin-documents-rescan.ts).
// Resets the document's scan state machine: status flips back to 'pending-scan'
// and scannedAt/scanResult are cleared, so the scan pipeline re-drives it. The
// firm-facing use case is a STUCK or wrongly-quarantined document ('pending-scan'
// that never resolved, or 'rejected-*') — the vault offers it on exactly those
// rows. Step-up + admin/compliance/super_admin gated server-side; the document
// is resolved in the CALLER'S OWN tenant (a firm act-as session cannot pass the
// step-up check — act-as tokens carry no mfa_challenge_at — so the vault
// disables the control under act-as rather than offering a dead end).

export interface RescanDocumentResponse {
  document: DocumentView;
  correlationId: string;
}

export function useRescanDocument() {
  const qc = useQueryClient();
  return useMutation<
    RescanDocumentResponse,
    ApiError,
    { documentId: string; trustId?: string }
  >({
    mutationFn: ({ documentId }) =>
      api<RescanDocumentResponse>(
        `/api/v1/admin/documents/${encodeURIComponent(documentId)}/rescan`,
        { method: 'POST' },
      ),
    onSuccess: (_data, vars) => {
      // Refresh the row's scan status wherever it renders — the flat admin list
      // and, when fired from a trust vault, that trust's own register.
      void qc.invalidateQueries({ queryKey: ['documents'] });
      if (vars.trustId) {
        void qc.invalidateQueries({ queryKey: ['trusts', 'documents', vars.trustId] });
      }
    },
  });
}

// ── Delete (soft-delete, own-upload self-cleanup) ────────────────────────────
//
// `DELETE /v1/documents/{id}` (handlers/documents-delete.ts). Authorised for
// admin/compliance/rm/director/super_admin, OR the ORIGINAL UPLOADER of the
// document, OR a trustee-tier member of its trust — so a non-admin caller can
// always remove a document THEY just uploaded, even when a subsequent,
// admin-gated write (e.g. appending a Letter of Wishes version) rejects them
// with 403. Used by the LoW "Add letter" modal to clean up the just-finalized
// attachment when the LoW-append step fails, so a rejected submission never
// leaves an orphaned document row behind (grader R1 row 16 / Finding 7).
export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { documentId: string; trustId?: string }>({
    mutationFn: ({ documentId }) =>
      api<void>(`/api/v1/documents/${encodeURIComponent(documentId)}`, { method: 'DELETE' }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
      if (vars.trustId) {
        void qc.invalidateQueries({ queryKey: ['trusts', 'documents', vars.trustId] });
      }
    },
  });
}
